import { useState, useEffect } from 'react';
import { Shield, Bot, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { hasPermission } from '@/lib/permissions';
import { AdminRole } from '@/types';

interface BotConfig {
  id: string;
  platform: string;
  token: string;
  webhook_url: string;
  welcome_message: string;
  is_active: boolean;
  max_daily_messages: number;
  created_at: string;
  updated_at: string;
}

export function BotConfigPage() {
  const [configs, setConfigs] = useState<BotConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAccess();
    fetchConfigs();
  }, []);

  const checkAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('admin_role')
      .eq('id', user.id)
      .single();

    if (!hasPermission(profile?.admin_role as AdminRole, 'manageSystem')) {
      toast.error('Insufficient permissions');
      return;
    }
  };

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('bot_configs')
        .select('*')
        .order('platform');

      if (error) throw error;
      setConfigs(data);
    } catch (error) {
      console.error('Error fetching bot configs:', error);
      toast.error('Failed to load bot configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (config: BotConfig) => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('bot_configs')
        .update({
          token: config.token,
          webhook_url: config.webhook_url,
          welcome_message: config.welcome_message,
          is_active: config.is_active,
          max_daily_messages: config.max_daily_messages,
          updated_at: new Date().toISOString()
        })
        .eq('id', config.id);

      if (error) throw error;
      toast.success('Bot configuration updated successfully');
      fetchConfigs();
    } catch (error) {
      console.error('Error updating bot config:', error);
      toast.error('Failed to update bot configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async (config: BotConfig) => {
    try {
      const response = await fetch(config.webhook_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'test',
          message: 'Test webhook connection'
        })
      });

      if (!response.ok) throw new Error('Webhook test failed');
      toast.success('Webhook test successful');
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast.error('Webhook test failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Bot Configuration</h1>
          <p className="text-muted-foreground">
            Manage messaging bot configurations and settings
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          <Shield className="h-4 w-4 mr-1" />
          Admin Panel
        </Badge>
      </div>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center h-48">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : (
        configs.map((config) => (
          <Card key={config.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Bot className="h-5 w-5" />
                  <span>{config.platform} Bot</span>
                </CardTitle>
                <Switch
                  checked={config.is_active}
                  onCheckedChange={(checked) => handleSave({ ...config, is_active: checked })}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">API Token</label>
                  <Input
                    type="password"
                    value={config.token}
                    onChange={(e) => setConfigs(configs.map(c => 
                      c.id === config.id ? { ...c, token: e.target.value } : c
                    ))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Max Daily Messages</label>
                  <Input
                    type="number"
                    value={config.max_daily_messages}
                    onChange={(e) => setConfigs(configs.map(c => 
                      c.id === config.id ? { ...c, max_daily_messages: parseInt(e.target.value) } : c
                    ))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Webhook URL</label>
                <div className="flex space-x-2">
                  <Input
                    value={config.webhook_url}
                    onChange={(e) => setConfigs(configs.map(c => 
                      c.id === config.id ? { ...c, webhook_url: e.target.value } : c
                    ))}
                  />
                  <Button 
                    variant="outline"
                    onClick={() => handleTestWebhook(config)}
                  >
                    Test
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Welcome Message</label>
                <Textarea
                  value={config.welcome_message}
                  onChange={(e) => setConfigs(configs.map(c => 
                    c.id === config.id ? { ...c, welcome_message: e.target.value } : c
                  ))}
                  rows={4}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => handleSave(config)}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}